"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { SupabaseClient, User } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";

type SupabaseContextValue = {
  supabase: SupabaseClient;
  user: User | null;
  /**
   * True until the first client-side auth resolution completes. Consumers must
   * wait for this to settle before treating a null user as "signed out",
   * otherwise they redirect authenticated users to sign-in on first render
   * (initialUser is null and getUser resolves asynchronously).
   */
  isLoading: boolean;
};

const SupabaseContext = createContext<SupabaseContextValue | undefined>(
  undefined,
);

type SupabaseProviderProps = {
  children: React.ReactNode;
  initialUser: User | null;
};

export function SupabaseProvider({
  children,
  initialUser,
}: SupabaseProviderProps) {
  const [supabase] = useState(() => createClient());
  const [user, setUser] = useState<User | null>(initialUser);
  const [isLoading, setIsLoading] = useState(initialUser === null);

  useEffect(() => {
    let isMounted = true;

    const syncUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!isMounted) {
        return;
      }
      setUser(error ? null : data.user);
      setIsLoading(false);
    };

    void syncUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void syncUser();
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  const value = useMemo<SupabaseContextValue>(
    () => ({
      supabase,
      user,
      isLoading,
    }),
    [supabase, user, isLoading],
  );

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
}

export function useSupabase() {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error("useSupabase must be used within a SupabaseProvider");
  }
  return context;
}

export function useSupabaseUser() {
  return useSupabase().user;
}

/** True until the first client-side auth resolution settles. */
export function useSupabaseAuthLoading() {
  return useSupabase().isLoading;
}
